import { ref, isRef } from "vue";
import router from "../router/index";
import { useRouter } from "vue-router";
import { MenuType, VisitType } from "../enums/AuditEnums";
import { useUserStateStore } from "../stores/userState";

/***** 연락처 마스킹 시작 *****/
// 핸드폰 번호 입력 시 자동으로 "-" 추가
export const formatPhoneNumber = (cellNumber) => {
  if (!cellNumber) return "";

  let value = cellNumber.replace(/\D/g, ""); // 숫자 이외의 문자 제거

  if (value.length > 3 && value.length <= 7) {
    value = `${value.slice(0, 3)}-${value.slice(3)}`;
  } else if (value.length > 7) {
    value = `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7, 11)}`;
  }

  return value;
};

export const formatKoreanLandline = (number) => {
  if (!number) return "";

  // 기존 '-' 제거
  const cleanNumber = number.replace(/-/g, "");

  // 11자리 미만이면 그대로 반환
  if (cleanNumber.length < 9) return number;

  // 한국 지역번호 리스트 (2자리 및 3자리 지역번호)
  const areaCodes2 = ["02"]; // 2자리 지역번호
  const areaCodes3 = [
    "031",
    "032",
    "033",
    "041",
    "042",
    "043",
    "044",
    "051",
    "052",
    "053",
    "054",
    "055",
    "061",
    "062",
    "063",
    "064",
  ]; // 3자리 지역번호

  let formattedNumber = "";

  // 2자리 지역번호 처리 (예: 02)
  if (cleanNumber.startsWith("02")) {
    if (cleanNumber.length === 10) {
      formattedNumber = `${cleanNumber.slice(0, 2)}-${cleanNumber.slice(2, 6)}-${cleanNumber.slice(6)}`;
    } else {
      formattedNumber = `${cleanNumber.slice(0, 2)}-${cleanNumber.slice(2, 5)}-${cleanNumber.slice(5)}`;
    }
  }
  // 3자리 지역번호 처리 (예: 031, 032)
  else if (areaCodes3.some((code) => cleanNumber.startsWith(code))) {
    if (cleanNumber.length === 11) {
      formattedNumber = `${cleanNumber.slice(0, 3)}-${cleanNumber.slice(3, 7)}-${cleanNumber.slice(7)}`;
    } else {
      formattedNumber = `${cleanNumber.slice(0, 3)}-${cleanNumber.slice(3, 6)}-${cleanNumber.slice(6)}`;
    }
  }
  // 그 외 번호는 그대로 반환
  else {
    formattedNumber = cleanNumber;
  }

  return formattedNumber;
};
// 전화번호 입력 시 자동으로 "-" 추가

/***** 연락처 마스킹 끝 *****/

/***** confrim 공통 함수 시작 *****/
export const confirmAction = (message) => {
  return confirm(message);
};
/***** confrim 공통 함수 끝 *****/

/*** Dialog 관련 함수 시작 ***/
export function useMsgDlg() {
  const dlgMsg = ref("");
  const dlgCaption = ref("");
  const dlgIcon = ref("");
  let show;
  let onCloseCallback = null; // ✅ 다이얼로그가 닫힐 때 실행할 콜백 저장

  const init = (isShow) => {
    show = isShow;
  };

  const showMsgDialog = (
    message,
    caption = "",
    callback = null,
    icon = "error",
  ) => {
    dlgMsg.value = message;
    dlgCaption.value = caption;
    show.value = true;
    onCloseCallback = callback; // ✅ 콜백 저장
    dlgIcon.value = icon;
  };

  const updateMsgDialog = (message, caption = null) => {
    if (!show?.value) return; // 다이얼로그가 열려있을 때만 갱신
    dlgMsg.value = message;
    if (caption !== null) {
      dlgCaption.value = caption;
    }
  };

  const closeMSGDlg = () => {
    show.value = false;
    if (onCloseCallback) {
      onCloseCallback(); // ✅ 콜백 실행 (라우팅 등)
      onCloseCallback = null; // ✅ 실행 후 콜백 초기화
    }
  };

  return {
    dlgMsg,
    dlgCaption,
    dlgIcon,
    showMsgDialog,
    init,
    closeMSGDlg,
    updateMsgDialog,
  };
}

export function useUnsavedChangeWarningDlg() {
  // dialog show 상태 (provide로 내려줄 ref)
  const showUnsavedDlg = ref(false);

  // Promise resolve를 저장해뒀다가 버튼 클릭 시 호출
  let resolver = null;

  // ✅ 다이얼로그 열고, 사용자가 누른 버튼 결과를 true/false로 받기
  const openUnsavedChangeWarning = () => {
    // 이미 열려있으면 기존 promise 정리(원하면 false로 처리)
    if (showUnsavedDlg.value && resolver) {
      resolver(false);
      resolver = null;
    }

    showUnsavedDlg.value = true;

    return new Promise((resolve) => {
      resolver = resolve;
    });
  };

  // ✅ "저장하지 않고 이동" (OK) 눌렀을 때
  const confirmLeaveWithoutSave = () => {
    showUnsavedDlg.value = false;
    if (resolver) {
      resolver(true);
      resolver = null;
    }
  };

  // ✅ "취소" 눌렀을 때 (또는 외부에서 강제 닫기)
  const cancelLeave = () => {
    showUnsavedDlg.value = false;
    if (resolver) {
      resolver(false);
      resolver = null;
    }
  };

  // ✅ 라우트 가드에서 await 하는 패턴이 많아서 helper로도 제공
  const confirmLeaveWithoutSaving = async () => {
    return await openUnsavedChangeWarning();
  };

  return {
    // state
    showUnsavedDlg,

    // open
    openUnsavedChangeWarning,
    confirmLeaveWithoutSaving,

    // handlers
    confirmLeaveWithoutSave,
    cancelLeave,
  };
}

/*** Dialog 관련 함수 끝 ***/

/** 숫자 관련 함수 시작 **/
export function addDecimalPlaces(num, decimalPlaces) {
  if (num === null || num === "" || isNaN(num) || isNaN(decimalPlaces)) return;

  const parsedNum = parseFloat(num);
  if (isNaN(parsedNum)) return; // 변환 후에도 NaN이면 반환

  return parsedNum.toFixed(decimalPlaces);
}

// 6자리 난수 발생
export const generateEmailVerificationCode = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

export const toNumber = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v)
    .trim()
    .replace(/[^0-9.+-]/g, ""); // 숫자/부호/소수점만 남김
  if (
    s === "" ||
    s === "+" ||
    s === "-" ||
    s === "." ||
    s === "+." ||
    s === "-."
  )
    return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};
/** 숫자 관련 함수 끝 **/

/*** 라우터 네비게이션 관련 함수 시작 ***/
export const openInNewTab = (path) => {
  // const routeUrl = router.resolve(path);
  window.open(path.href, "_blank");
};

// utils/useSecureNavigation.js
export function openSecureTab(routePath, payload, type = "subject") {
  const token = createToken();
  //   const token = crypto.randomUUID()
  localStorage.setItem(`${type}-token-${token}`, JSON.stringify(payload));

  const resolved = router.resolve(`${routePath}?token=${token}`);
  window.open(resolved.href, "_blank");
}

export function createToken() {
  return window.crypto?.randomUUID?.() ?? fallbackUUID();
}

function fallbackUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// 같은 탭에서 token 기반 navigate (router.push)
export function secureNavigate(routePath, payload, type = "study") {
  const token = window.crypto?.randomUUID?.() ?? fallbackUUID();
  localStorage.setItem(`${type}-token-${token}`, JSON.stringify(payload));
  router.push(`${routePath}?token=${token}`);
}

// router guard 안에서 next()에 넘길 path 문자열 생성용
export function createSecurePath(routePath, payload, type = "study") {
  const token = window.crypto?.randomUUID?.() ?? fallbackUUID();
  localStorage.setItem(`${type}-token-${token}`, JSON.stringify(payload));
  return `${routePath}?token=${token}`;
}
/*** 라우터 네비게이션 관련 함수 끝 ***/

/***** 나이 관련 함수 시작 *****/
export const calculateAge = (birthDateStr) => {
  const today = new Date();
  const birthDate = new Date(birthDateStr);

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  // 생일이 아직 안 지난 경우
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age;
};
/***** 나이 관련 함수 끝 *****/

/**** 반응평가 관련 함수 시작 ****/
export const calcSPDForLugano = (list, type) => {
  // type : 1 베이스라인 type 2 반응평가
  let spd = 0;
  list.forEach((item) => {
    const ldi = parseFloat(item.Ldi);
    const sdi = parseFloat(item.Sdi);
    const isNotNaN = !isNaN(ldi) && !isNaN(sdi);
    if (
      type === 1 &&
      item.ResponseType === "Lugano_TI" &&
      item.IsDel == false
    ) {
      if (isNotNaN) {
        spd += ldi * sdi;
      }
    } else if (type === 2) {
      if (isNotNaN) {
        spd += ldi * sdi;
      }
    }
  });

  return spd;
};

export const calcSPDForRecist = (list, type) => {
  // type : 1 베이스라인 type 2 반응평가
  let spd = 0;
  list.forEach((item) => {
    const ldi = parseFloat(item.Ldi);
    const isNotNaN = !isNaN(ldi);
    if (
      type === 1 &&
      item.ResponseType === "Recist_TI" &&
      item.IsDel == false
    ) {
      if (isNotNaN) {
        spd += ldi;
      }
    } else if (type === 2) {
      if (isNotNaN) {
        spd += ldi;
      }
    }
  });

  return spd;
};

/**** 반응평가 관련 함수 끝 ****/

/**** Audit 관련 함수 시작 ****/
export const isDataEntryComplete = (site, dataMap) => {
  const checkRules = {
    visitReulst: {
      visitVitalResultList: (item) => {
        if (item.ResultData1 != "" && item.ResultData1 != null) {
          return true;
        }
        return false;
      },
      visitSubjectRegimen: (item) =>
        item.Dose != null &&
        item.Dose != "" &&
        item.Intensity !== null &&
        item.Intensity !== "",
    },
    rtxVisitReulst: {
      visitVitalResultList: (item) => {
        if (item.ResultData1 != "" && item.ResultData1 != null) {
          return true;
        }
        return false;
      },
    },
    lab: {
      visitLabResultList: (item) =>
        item.ResultData1 != null &&
        item.ResultData1 != "" &&
        item.ResultData2 !== null &&
        item.ResultData2 !== "",
    },
    // 필요시 다른 site 추가
  };

  const rulesForSite = checkRules[site];
  if (!rulesForSite) {
    console.warn(`[isDataEntryComplete] Unknown site: ${site}`);
    return false;
  }

  return Object.entries(rulesForSite).every(([listKey, checker]) => {
    const list = dataMap[listKey];
    if (!list) {
      console.warn(`[isDataEntryComplete] Missing list: ${listKey}`);
      return false;
    }
    const arr = isRef(list) ? list.value : list;
    return Array.isArray(arr) && arr.length > 0 && arr.every(checker);
  });
};

export function useAuditDialog() {
  const showAuditDlg = ref(false);
  const userState = useUserStateStore();
  const auditData = ref({
    SubjectID: null, // 나중에 주입
    LockID: null,
    AuditType: 1, // 예: 1~5 (라디오 그룹)
    AuditText: "", // 예: 메모
    AuditLink: "",
    StudyID: null,
    SVisitID: null,
    VisitType: "",
    VisitSubNo: null,
    SignedUserID: "",
    MeaningText: "",
    LegalNoticeText: "",
    UserPW: "",
    IPAddress: "unknown",
  });

  let _resolve = null;

  // menu 0 : 스크리닝 관리
  // menu 1 : 방문 관리
  // menu 2 : EOS
  const openDialog = async (
    subjectID,
    subjectName,
    visit,
    type,
    lockID,
    menu = 0,
    studyID,
    SVisitID,
    visitType,
    visitSubNo,
    tabName = "",
  ) => {
    let link = "";
    switch (menu) {
      case MenuType.SCREENING: // 스크리닝 관리
        if (tabName) {
          link = `${subjectName} - 스크리닝 - ${tabName}`;
        } else if (type == VisitType.DIAGNOSIS_STATUS) {
          link = `${subjectName} -  스크리닝 - 진단`;
        } else if (type == VisitType.SUBJECT_STATUS) {
          link = `${subjectName} - 스크리닝 - 대상자 상태`;
        } else if (type == VisitType.COMORBODITY) {
          link = `${subjectName} - 스크리닝 - 동반질환`;
        } else if (type == VisitType.PRE_CART_DISEASE_INFO) {
          link = `${subjectName} - 스크리닝 - CAR T-세포치료 직전의 질환 정보`;
        }
        break;
      case MenuType.VISIT: // 방문 관리
        if (tabName) {
          link = `${subjectName} - 방문관리 - ${tabName} - ${visit.VisitName}`;
        } else if (type == VisitType.VISITING_DATA) {
          // 방문별 자료
          link = `${subjectName} - 방문관리- 방문별 자료(Visiting Data) - ${visit.VisitName}`;
        } else if (type == VisitType.LABORATORY) {
          // 검사 및 수행 상태
          link = `${subjectName} - 방문관리- 검사 및 수행 상태 - ${visit.VisitName}`;
        } else if (type == VisitType.ADVERSE_EVENT) {
          // 이상반응
          link = `${subjectName} - 방문관리- 이상반응 - ${visit.VisitName}`;
        } else if (type == VisitType.EVALUATION) {
          // 이차 종양·임신 평가
          link = `${subjectName} - 방문관리- 이차 종양·임신 평가 - ${visit.VisitName}`;
        } else if (type == VisitType.AFTER_TREATMENT) {
          // CAR T-세포 주입 후 치료
          link = `${subjectName} - 방문관리- CAR T-세포 주입 후 치료 - ${visit.VisitName}`;
        } else if (type == VisitType.AT_RELAPSE) {
          // CAR T-세포 치료 이후 재발시 환자 상태
          link = `${subjectName} - 방문관리- CAR T-세포 치료 이후 재발시 환자 상태 - ${visit.VisitName}`;
        } else if (type == VisitType.COMPLICATION_PROPHYLAXIS) {
          // CAR T-세포 합병증 예방요법
          link = `${subjectName} - 방문관리- CAR T-세포 합병증 예방요법 - ${visit.VisitName}`;
        } else if (type == VisitType.RESSPONSE_EVAL) {
          // 질병상태 및 반응평가
          link = `${subjectName} - 방문관리- 질병상태 및 반응평가 - ${visit.VisitName}`;
        }
        break;
      case MenuType.EOS: //
        link = `${subjectName} - EOS`;
        break;
    }

    const ip = await fetchIp();

    // ✅ 다이얼로그 열기 전에 초기화
    auditData.value = {
      SubjectID: subjectID ?? null,
      LockID: lockID,
      AuditType: 1,
      AuditText: "",
      AuditLink: link,
      StudyID: studyID,
      SVisitID: SVisitID,
      VisitType: visitType,
      VisitSubNo: visitSubNo,
      SystemUserID: userState.user.SystemUserID,
      SignedUserID: userState.user.UserID,
      MeaningText: "",
      LegalNoticeText: "",
      UserPW: "",
      IPAddress: ip || "unknown",
    };

    return new Promise((resolve) => {
      _resolve = resolve;
      showAuditDlg.value = true;
    });
  };

  const confirm = () => {
    showAuditDlg.value = false;
    _resolve?.({ confirmed: true, data: auditData.value });
    _resolve = null;
  };

  const cancel = () => {
    showAuditDlg.value = false;
    _resolve?.({ confirmed: false });
    _resolve = null;
  };

  return {
    showAuditDlg,
    auditData,
    openDialog,
    confirm,
    cancel,
  };
}

/**** Audit 관련 함수 끝 ****/

/**** CART 계산식 관련 함수 끝 ****/
export function calcFLIPI() {}

export function calcFLIPIIndex2() {}
/**** CART 계산식 함수 끝 ****/

/**** 인코딩 관련 함수 시작 ****/
export function decodeBase64(value) {
  if (!value) return "";
  try {
    return btoa(unescape(encodeURIComponent(value)));
  } catch (e) {
    console.warn("ActionID base64 decode 실패, 원문 사용", e);
    return value;
  }
}

/**** 인코딩 관련 함수 끝 ****/

/**** IP 관련 함수 시작 ****/
export const fetchIp = async () => {
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const { ip } = await res.json();
    return ip;
  } catch (e) {
    console.warn("IP 조회 실패", e);
    return "";
  }
};
/**** IP 관련 함수 끝 ****/

/**** 날짜 관련 함수 시작 ****/
export function parseYmd(dateStr) {
  if (!dateStr) return null;

  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return null;

  return new Date(y, m - 1, d);
}

export function formatYmd(date) {
  if (!(date instanceof Date)) return "";

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
}

export function addDays(date, days) {
  if (!(date instanceof Date)) return null;
  const d = new Date(date); // ✅ 원본 보호 (중요)
  d.setDate(d.getDate() + Number(days));
  return d;
}
/**** 날짜 관련 함수 끝 ****/

/**** 객체 비교 공통 함수 시작 ****/
export function isDifferentObject(a, b) {
  if (!a || !b) return false;

  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);

  for (const key of keys) {
    // 비교 제외할 필드
    if (["SCRID", "EOSID"].includes(key)) continue;

    if (a[key] !== b[key]) {
      return true;
    }
  }

  return false;
}

/**** 객체 비교 공통 함수 끝 ****/

/**** 객체 비교 공통 함수 시작 ****/
export function clonePlain(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export function takeSnapshot(snapshotRef, source) {
  snapshotRef.value = clonePlain(source);
}
/**** 객체 비교 공통 함수 끝 ****/

/**** 정렬함수 시작 ****/
export function defineSortableList(dataList = []) {
  const list = ref(dataList);

  /**
   * 확장 메서드: sortBy
   * @param {Array} sortByState - Vuetify의 [{ key, order }]
   * @param {Object} options - { defaultKey, bottomKey}
   * @param {Object} keyMap - header의 key와value가 다를시 매핑된값
   */
  list.sortBy = (sortByState, options = {}, keyMap) => {
    const { defaultKey = "rn", bottomKey, bottomValue } = options;

    if (!list.value || list.value.length === 0) return;

    // 1. 기본 정렬 (정렬 상태가 없을 때)
    if (!sortByState || sortByState.length === 0) {
      list.value.sort((a, b) => {
        let valA = a[defaultKey].toString();
        let valB = b[defaultKey].toString();
        return valA.localeCompare(valB, undefined, { numeric: true });
      });
      return;
    }

    const { key, order } = sortByState[0];
    const isAsc = order === "asc";
    const sortKey = keyMap[key] || key;

    // 2. 실제 정렬 수행
    list.value.sort((a, b) => {
      // 1순위: 설정된 bottomKey의 값이 bottomValue와 동일하면 해달 열은 하단으로
      if (a[bottomKey] !== b[bottomKey]) {
        if (a[bottomKey] === bottomValue) return 1;
        if (b[bottomKey] === bottomValue) return -1;
      }

      // 문자열 비교
      if (a[sortKey] > b[sortKey]) return isAsc ? 1 : -1;
      if (a[sortKey] < b[sortKey]) return isAsc ? -1 : 1;
      return 0;
    });

    // Vue 반응성 시스템에 변경 알림 (필요 시 복사본 할당)
    list.value = [...list.value];
  };

  return list;
}
/**** 정렬함수 끝 ****/

/**** S3 파일다운로드 시작 ****/
export async function s3FileDownload(res, file) {
  downloadBlob(new Blob([res]), file.AttachedFileName);
}

// 브라우저에서 만든 데이터(Blob / Uint8Array)를 파일로 내려받는다. (통계 엑셀 등)
export function downloadBlob(data, fileName, mimeType = "application/octet-stream") {
  const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName); // 다운로드 파일명 지정
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // ✅ 메모리 해제
  window.URL.revokeObjectURL(url);
}
/**** S3 파일다운로드 끝 ****/
